import { Dialog, Transition } from '@headlessui/react';
import React, { Fragment } from 'react';
import { get, useFormContext } from 'react-hook-form';
import { Divider } from '../../../../../../components/design/divider/Divider';
import { Input } from '../../../../../../components/design/Input';
import { WarningIcon } from '../../../../../../components/icons/WarningIcon';
import type { PlanInputForm } from '../../CreatePlanForm/create-plan-form-types/plan-input-form';

type UsageProductGroupDialogFormProps = {
  open: boolean;
  onClose?: () => void;
  productIndex: number;
  onCreateClick?: () => void;
};

export const UsageProductGroupDialogForm: React.FC<UsageProductGroupDialogFormProps> = ({
  open,
  onClose,
  productIndex,
  onCreateClick,
}) => {
  const { register, formState: { errors }, resetField } = useFormContext<PlanInputForm>();

  const productNameRegister = register(`usageProducts.${productIndex}.name`);

  const handleClose = () => {
    onClose?.();
  };

  const handleCancel = () => {
    resetField(productNameRegister.name, { defaultValue: '' });
    handleClose();
  };

  const handleCreate = () => {
    onCreateClick?.();
    handleClose();
  };

  return (
    <Transition
      appear
      show={open}
      as={Fragment}
    >
      <Dialog
        as="div"
        className="relative z-10"
        onClose={handleClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className="w-full max-w-md overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all"
              >
                <Dialog.Title
                  as="h3"
                  className="text-lg font-medium leading-6"
                >
                  Grouped Products
                </Dialog.Title>

                <Dialog.Description className="mt-2 text-sm">
                  You can group products to add variants and apply fees to the entire group.
                </Dialog.Description>

                <Divider className="my-5" />

                <div className="mt-5 flex flex-col gap-y-4">
                  <Input
                    {...productNameRegister}
                    label="Group name"
                    placeholder="i.e Active accounts EU & AU"
                    errorMessage={get(errors, productNameRegister.name)?.message}
                  />
                </div>

                <div className="mt-4 flex justify-end gap-x-2">
                  <button
                    type="button"
                    className="border-grayed-out text-grayed-out hover:bg-grayed-out
                            inline-flex justify-center rounded-md border px-4 py-2 text-sm font-medium hover:bg-opacity-10 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>

                  <button
                    onClick={handleCreate}
                    className="inline-flex justify-center rounded-md border
                            border-transparent bg-blue-100 px-4 py-2 text-sm font-medium text-blue-900 hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                  >
                    Create
                  </button>
                </div>

                <Divider className="my-5" />

                <div className="flex gap-x-2">
                  <WarningIcon />
                  <p className="text-sm">Commitment and base amount defined will apply on all variants associated with the group</p>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
